/**
 * mediaTranscodeService.ts
 *
 * WebCodecs transcoding via mediabunny — shrinks video/screen recordings
 * client-side before upload (native phone screen recordings run 6-12 Mbps;
 * 15-20 min ≈ 700MB-1.5GB, unusable over mobile networks).
 *
 * Output is always WebM (VP9 → VP8 fallback / Opus): best compression for
 * screen content, and Google STT reads WebM/Opus — so transcoded
 * submissions become transcribable (the native mp4/AAC ones are not).
 *
 * Design contract (docs/superpowers/specs/2026-07-29-media-compression-design.md):
 * this service only ever ADDS capability. Callers must treat any throw —
 * including 'transcode-unsupported' — as "use the fallback path", never as
 * a submission failure. Resolution is left untouched: bitrate is the size
 * lever, and downscaling screen recordings destroys the UI legibility the
 * screen tasks exist to capture.
 */
import {
  Input,
  Output,
  Conversion,
  ALL_FORMATS,
  BlobSource,
  WebMOutputFormat,
  BufferTarget,
  canEncodeVideo,
  canEncodeAudio,
} from 'mediabunny';

export type TranscodeKind = 'screen' | 'video';

/** Bitrate/framerate targets. Screen tasks are scroll-heavy (shop-along,
    browse-along), NOT static UI — 15 fps read as choppy and 1.2 Mbps smeared
    text during scroll at native phone resolutions (raised on Kapil's call,
    2026-08-04). ~19 MB/min; a 10-min recording ≈ 190 MB, under the 500 MB
    storage rule. */
const TARGETS: Record<TranscodeKind, { videoBitrate: number; frameRate: number }> = {
  screen: { videoBitrate: 2_500_000, frameRate: 30 },
  // 5 Mbps VP9 @ 1080p30 — HD presentation quality for camera footage
  // (raised from 3.5 on Kapil's call, 2026-07-29). A 10-min video ≈ 375 MB
  // upload; still under the 500 MB storage rule.
  video: { videoBitrate: 5_000_000, frameRate: 30 },
};
const AUDIO_BITRATE = 64_000;

let cachedCodec: 'vp9' | 'vp8' | null | undefined;

/** Pick the best supported encoder, once per session. null = no transcode. */
async function pickVideoCodec(): Promise<'vp9' | 'vp8' | null> {
  if (cachedCodec !== undefined) return cachedCodec;
  try {
    if (typeof VideoEncoder === 'undefined' || !(await canEncodeAudio('opus'))) {
      cachedCodec = null;
    } else if (await canEncodeVideo('vp9')) {
      cachedCodec = 'vp9';
    } else if (await canEncodeVideo('vp8')) {
      cachedCodec = 'vp8';
    } else {
      cachedCodec = null;
    }
  } catch {
    cachedCodec = null;
  }
  return cachedCodec;
}

export async function canTranscode(): Promise<boolean> {
  return (await pickVideoCodec()) !== null;
}

/**
 * Transcode a video/screen blob to WebM. Throws 'transcode-unsupported' when
 * the device can't (caller falls back), DOMException 'AbortError' on cancel,
 * and whatever mediabunny throws on real failures (caller falls back).
 */
export async function transcodeMedia(
  blob: Blob,
  kind: TranscodeKind,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<Blob> {
  const codec = await pickVideoCodec();
  if (!codec) throw new Error('transcode-unsupported');
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const input = new Input({ source: new BlobSource(blob), formats: ALL_FORMATS });
  const target = new BufferTarget();
  const output = new Output({ format: new WebMOutputFormat(), target });

  const t = TARGETS[kind];
  const conversion = await Conversion.init({
    input,
    output,
    video: { codec, bitrate: t.videoBitrate, frameRate: t.frameRate },
    audio: { codec: 'opus', bitrate: AUDIO_BITRATE },
  });

  if (!conversion.isValid) throw new Error('transcode-invalid-conversion');

  conversion.onProgress = (progress) => onProgress?.(Math.round(progress * 100));

  const onAbort = () => { conversion.cancel().catch(() => {}); };
  signal?.addEventListener('abort', onAbort, { once: true });

  try {
    await conversion.execute();
  } catch (err) {
    // Cancellation (ours or via signal) → normalize to AbortError so the
    // upload manager's existing cancel handling applies unchanged.
    if (signal?.aborted || (err instanceof Error && err.name === 'ConversionCanceledError')) {
      throw new DOMException('Aborted', 'AbortError');
    }
    throw err;
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }

  if (!target.buffer) throw new Error('transcode-empty-output');
  const result = new Blob([target.buffer], { type: 'video/webm' });

  // A transcode that GREW the file (already-efficient input) is a no-op win:
  // keep the original. Also keeps mp4 playback for those files.
  return result.size < blob.size ? result : blob;
}
